import { useTranslation } from '@pasteltech/react-i18n'
import { memo, useMemo } from 'react'
import styled from 'styled-components'
import {
  AlarmIcon,
  AlternateEmailIcon,
  CallIcon,
  InstagramIcon,
  LargeFacebookIcon,
  LocationIcon,
  WhatsappIcon,
} from '../../svg-components'
import { DeepNullable, ScreenQuery } from '../../utilities'
import { ExternalLink } from '../external-link'
import { StrapiImage, StrapiImageData } from '../strapi-image'

const Container = styled.div`
  display: flex;
  background-color: ${({ theme }) => theme.colors.container.highest};
  ${ScreenQuery.mobile} {
    flex-direction: column;
  }
  ${ScreenQuery.tablet} {
    flex-direction: column;
  }
`
const LeftContainer = styled.div`
  flex: 0 0 50%;
  display: flex;
  justify-content: flex-end;
  ${ScreenQuery.mobile} {
    flex: 0 0 auto;
    width: 100%;
    margin: 0 auto;
  }
  ${ScreenQuery.tablet} {
    flex: 0 0 auto;
    width: 100%;
    margin: 0 auto;
  }
`
const RightContainer = styled.div`
  flex: 0 0 50%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: ${({ theme }) => theme.sizes.spacings.xl}px;
  background-color: ${({ theme }) => theme.colors.container.highest};
  ${ScreenQuery.mobile} {
    margin: 0 auto;
  }
  ${ScreenQuery.tablet} {
    margin: 0 auto;
  }
`
const Row = styled.div`
  display: flex;

  gap: ${({ theme }) => theme.sizes.spacings.lg}px;
  ${ScreenQuery.desktop} {
    margin-left: ${({ theme }) => theme.sizes.spacings.xxxxl}px;
  }
  ${ScreenQuery.mobile} {
    flex-direction: column;
    width: 90%;
    margin: 0 auto;
  }
  ${ScreenQuery.tablet} {
    flex-direction: column;
    width: 90%;
    margin: 0 auto;
  }
`
const StyledWhatsappIcon = styled(WhatsappIcon)`
  path {
    fill: ${({ theme }) => theme.colors.secondary.main};
  }
`
const IconContainer = styled.div`
  padding: ${({ theme }) => theme.sizes.spacings.lg}px;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: ${({ theme }) => theme.sizes.spacings.sm}px;
  background-color: ${({ theme }) => theme.colors.container.lowest};
`
const TextContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  color: ${({ theme }) => theme.colors.primary.mainContrast};
  gap: ${({ theme }) => theme.sizes.spacings.sm}px;
`
const Detail = styled.div`
  display: flex;
  align-items: center;
  padding: ${({ theme }) => theme.sizes.spacings.lg}px;
  gap: ${({ theme }) => theme.sizes.spacings.lg}px;
  ${ScreenQuery.mobile} {
    display: flex;
    flex-direction: column;
    text-align: center;
  }
  ${ScreenQuery.tablet} {
    display: flex;
    flex-direction: column;
    text-align: center;
  }
`
const Name = styled.div`
  width: 100%;
`
const Value = styled.div`
  width: 100%;
`
const FollowUsText = styled.div`
  color: ${({ theme }) => theme.colors.primary.mainContrast};
`
const StyledStrapiImage = styled(StrapiImage)`
  width: 100%;
  height: 100%;
  object-fit: fill;
`
const StyledExternalLink = styled(ExternalLink)`
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.primary.mainContrast};
  &:hover {
    cursor: pointer;
    opacity: 0.8;
  }
  &:active {
    opacity: 0.6;
  }
`
const WhatsappLink = styled(StyledExternalLink)`
  text-decoration: underline;
`
const GoogleMapLink = styled(ExternalLink)`
  width: 100%;
  height: 100%;
  &:hover {
    cursor: pointer;
  }
`

type LocationAndContactData = null | DeepNullable<{
  address: string
  phone: string
  whatsapp: string
  whatsappLink: string
  facebookLink: string
  instagramLink: string
  email: string
  workHour: string
  googleMapLink: string
  googleMapImage: StrapiImageData
}>
type Props = {
  data: LocationAndContactData
}
const IconsContainer = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.sizes.spacings.sm}px;
`

export const LocationAndContact = memo(({ data }: Props) => {
  const { t } = useTranslation()

  const rows = useMemo(
    () => [
      [
        {
          icon: <LocationIcon />,
          title: t('contact.address'),
          content: data?.address,
        },
      ],
      [
        {
          icon: <CallIcon />,
          title: t('contact.phone'),
          content: data?.phone,
        },
        {
          icon: <StyledWhatsappIcon />,
          title: t('button.whatsapp'),
          content: (
            <WhatsappLink to={data?.whatsappLink}>
              {data?.whatsapp}
            </WhatsappLink>
          ),
        },
      ],
      [
        {
          icon: <AlternateEmailIcon />,
          title: t('contact.email'),
          content: data?.email,
        },
      ],
      [
        {
          icon: <AlarmIcon />,
          title: t('contact.workingHours'),
          content: data?.workHour,
        },
      ],
    ],
    [
      data?.address,
      data?.email,
      data?.phone,
      data?.whatsapp,
      data?.whatsappLink,
      data?.workHour,
      t,
    ],
  )

  return (
    <Container>
      <LeftContainer>
        <GoogleMapLink to={data?.googleMapLink ?? ''}>
          <StyledStrapiImage strapiImage={data?.googleMapImage} />
        </GoogleMapLink>
      </LeftContainer>
      <RightContainer>
        {rows.map((row) => (
          <Row key={row.map((cell) => cell.title).join('-')}>
            {row.map((cell) => (
              <Detail key={cell.title}>
                <IconContainer>{cell.icon}</IconContainer>
                <TextContainer>
                  <Name>{cell.title}</Name>
                  <Value>{cell.content}</Value>
                </TextContainer>
              </Detail>
            ))}
          </Row>
        ))}
        <Row>
          <Detail>
            <FollowUsText>{t('contact.followUs')}</FollowUsText>
            <IconsContainer>
              <StyledExternalLink to={data?.facebookLink ?? ''}>
                <LargeFacebookIcon />
              </StyledExternalLink>
              <StyledExternalLink to={data?.instagramLink ?? ''}>
                <InstagramIcon />
              </StyledExternalLink>
            </IconsContainer>
          </Detail>
        </Row>
      </RightContainer>
    </Container>
  )
})

LocationAndContact.displayName = 'LocationAndContact'
